// src/components/Navbar.jsx
// Responsive Chakra v3 navbar: uses CollapsibleRoot/CollapsibleContent + lucide icons.

import React from "react";
import {
  Box,
  Container,
  Flex,
  HStack,
  Stack,
  IconButton,
  Button,
  Link as ChakraLink,
  Image,
  Icon,
  CollapsibleRoot,
  CollapsibleContent,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/tax-relief-companies", label: "Companies" },
  { to: "/tax-relief-blog", label: "Blog" },
  { to: "/tax-relief-calculator", label: "Calculator" },
];

function NavItem({ to, children, onClick }) {
  const { pathname } = useLocation();
  const active = pathname === to;

  const color = active
    ? { base: "blue.700", _dark: "blue.200" }
    : { base: "gray.700", _dark: "gray.300" };

  const bg = active
    ? { base: "blue.50", _dark: "whiteAlpha.200" }
    : { base: "transparent", _dark: "transparent" };

  const hoverBg = { base: "gray.100", _dark: "whiteAlpha.200" };

  return (
    <ChakraLink
      as={RouterLink}
      to={to}
      onClick={onClick}
      px={3}
      py={2}
      rounded="md"
      fontWeight={600}
      color={color}
      bg={bg}
      _hover={{ textDecoration: "none", bg: hoverBg }}
    >
      {children}
    </ChakraLink>
  );
}

export default function Navbar() {
  const [isOpen, setOpen] = React.useState(false);
  const toggle = () => setOpen((v) => !v);

  const headerBg = { base: "white", _dark: "gray.900" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };

  return (
    <Box
      as="header"
      position="sticky"
      top="0"
      zIndex="1000"
      bg={headerBg}
      borderBottom="1px solid"
      borderColor={border}
      backdropFilter="saturate(180%) blur(8px)"
    >
      <Container maxW="6xl" py={2}>
        <Flex align="center" justify="space-between" minH="64px">
          {/* Brand */}
          <ChakraLink
            as={RouterLink}
            to="/"
            display="inline-flex"
            alignItems="center"
          >
            {/* mobile logo */}
            <Image
              src="/images/logo.png"
              alt="Consumer Advocate Reviews"
              h="100px"
              display={{ base: "block", md: "none" }}
            />
            {/* desktop wordmark */}
            <Image
              src="/images/logo-text.png"
              alt="Consumer Advocate Reviews"
              h="100px"
              display={{ base: "none", md: "block" }}
            />
          </ChakraLink>

          {/* Desktop nav */}
          <HStack spacing={2} display={{ base: "none", md: "flex" }}>
            {NAV.map((item) => (
              <NavItem key={item.to} to={item.to}>
                {item.label}
              </NavItem>
            ))}

            <Button
              as={RouterLink}
              to="/tax-relief-companies"
              colorScheme="blue"
              ml={2}
            >
              Get Matched
            </Button>
          </HStack>

          {/* Mobile toggle */}
          <IconButton
            aria-label={isOpen ? "Close menu" : "Open menu"}
            variant="ghost"
            display={{ base: "inline-flex", md: "none" }}
            onClick={toggle}
          >
            <Icon as={isOpen ? X : Menu} boxSize={5} />
          </IconButton>
        </Flex>

        {/* Mobile menu (v3 Collapsible) */}
        <CollapsibleRoot open={isOpen}>
          <CollapsibleContent>
            <Box pb={4} display={{ md: "none" }}>
              <Stack spacing={1}>
                {NAV.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </NavItem>
                ))}
                <Button
                  as={RouterLink}
                  to="/tax-relief-companies"
                  colorScheme="blue"
                  mt={2}
                  onClick={() => setOpen(false)}
                >
                  Get Matched
                </Button>
              </Stack>
            </Box>
          </CollapsibleContent>
        </CollapsibleRoot>
      </Container>
    </Box>
  );
}
